import { z } from "zod";

export const vehicleFormSchema = z.object({
  modelId: z.string().min(1, "Selecciona el modelo"),
  year: z
    .string()
    .trim()
    .refine((value) => /^\d{4}$/.test(value), "Ingresa un año válido"),
  plate: z.string().trim().min(1, "Ingresa la placa"),
  colorId: z.string().optional(),
  sapCode: z.string().optional(),
  acquiredOn: z.string().optional(),
  // PLAN-007 §7 (fase 3, 2026-09-21): el valor ya no lo escribe el operador
  // a mano — lo devuelve `uploadImage` (`@portal/common/fileUpload`) tras
  // subir el archivo elegido con `VehiclesPanel`'s `FileUploadChip`. El
  // contrato sigue exigiendo `format: uri` en `photoUrl`, así que la
  // validación de forma se conserva sin cambios.
  // QA-MTZ-002 #24: el contrato exige `format: uri`; sin esta validación el
  // cliente manda cualquier texto y el 400 opaco del servidor llega sin
  // señalar el campo. `""` no pasa por `z.string().url()` (rechaza cadena
  // vacía), así que la cadena vacía se normaliza a `undefined` antes de
  // validar el formato — el campo sigue siendo opcional, solo se exige que,
  // si viene, sea una URL.
  photoUrl: z
    .string()
    .optional()
    .transform((value) => (value?.trim() ? value.trim() : undefined))
    .refine(
      (value) =>
        value === undefined || z.string().url().safeParse(value).success,
      {
        message: "Ingresa una URL válida",
      },
    ),
  // PLAN-007 Fase 4d: id del archivo ya subido y confirmado
  // (`uploadImage`/`FileUploadChip`). Tiene prioridad sobre `photoUrl` en el
  // servidor (VehicleInput.photoFileId, 20-members-points.yaml); se manda
  // solo cuando el operador sube un archivo nuevo en esta sesión del
  // formulario — al editar sin tocar la imagen queda `undefined` y se sigue
  // enviando la `photoUrl` heredada tal cual estaba.
  photoFileId: z.string().optional(),
});
export type VehicleFormValues = z.infer<typeof vehicleFormSchema>;

// SPEC-203 §3: la baja exige motivo del catálogo `inactivation-reasons`.
export const deactivateVehicleSchema = z.object({
  deactivationReasonId: z.string().min(1, "Indica el motivo de la baja"),
});

// SPEC-203 §4 / §6: `ACTUAL_DATE_REQUIRED` — un mantenimiento en estado
// "realizado" exige fecha real y kilometraje. El kilometraje menor al último
// registrado (`LOWER_MILEAGE`) es un aviso del servidor, no una regla de
// cliente: no se valida acá.
export const maintenanceFormSchema = z
  .object({
    maintenanceTypeId: z.string().min(1, "Selecciona el tipo"),
    status: z.enum(["scheduled", "completed"]),
    estimatedDate: z.string().optional(),
    actualDate: z.string().optional(),
    mileage: z.string().optional(),
    workshopId: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.status !== "completed") return;
    const message =
      "Un mantenimiento realizado requiere fecha real y kilometraje";
    if (!value.actualDate?.trim())
      ctx.addIssue({ code: "custom", path: ["actualDate"], message });
    if (!value.mileage?.trim())
      ctx.addIssue({ code: "custom", path: ["mileage"], message });
  });
export type MaintenanceFormValues = z.infer<typeof maintenanceFormSchema>;
